import { Directive, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable, Subscription, from } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * A base class to consistently handle form
 * submissions that require async loading
 * and error handling
 */
@Directive()
export abstract class SubmittableForm implements OnDestroy
{
  form: FormGroup | undefined;
  error: any;
  private autoCapitalizeSubs: Subscription;

  constructor(
    protected translateService: TranslateService
  )
  {
    this.autoCapitalizeSubs = new Subscription();
  }

  /**
   * A simple abstraction for submitting a form
   * that provides the inherited class with a loader and optional message
   * and error handling using the alert controller with a simple ok button
   * to dismiss.
   *
   * @param exec A function that returns an observable or promise to execute
   * @param options (optional) Extra parameters that can be set to alter the behaviour
   * @param loadingMsg (optional) A translation key to display during the loader presentation
   * @param onSuccess (optional) A function that runs on the success of the exec function
   * @param onError (optional) A function that overrides the default error implementation
   * @param onErrorPredicate (optional) A predicate to control which errors the implementation class
   * wants to handle.
   */
  protected async onConfirm(
    exec: () => (Observable<any> | PromiseLike<any>),
    options?: {
      loadingMsg?: string,
      onSuccess?: () => void,
      onError?: (error: any) => void,
      onErrorPredicate?: (error: any) => boolean,
    })
  {
    if ((this.form) && 
        (this.form.invalid))
    {
      return;
    }

    try
    {
      const error = await from(exec()).toPromise();
      if (error && options && options.onError && (!options.onErrorPredicate || options.onErrorPredicate(error)))
      {
        options.onError(error);
      }
      else if (error)
      {
        this.error = error;
      }
      else
      {
        this.error = undefined;
        if (options && options.onSuccess)
        {
          options.onSuccess();
        }
      }
    }
    catch (error: any)
    {
      let reason: string;

      if ((options) && 
          (options.onError ) && 
          (!options.onErrorPredicate || options.onErrorPredicate(error)))
      {
        options.onError(error);
        return;
      }

      if (typeof error === 'string')
      {
        reason = error;
      }
      else if (error.translatableMessage && typeof error.translatableMessage.toPromise === 'function')
      {
        reason = await error.translatableMessage.toPromise();
      }
    }
  }

  private capitalizeWords(control: AbstractControl, str: string)
  {
    control.patchValue(str.replace(/(\b[a-z](?!\s))/g, letter => letter.toUpperCase()));
  }

  private _matchValues(control: AbstractControl, matchControlName: string): boolean {
    return !!control.parent && !!control.parent.value &&
            control.value === control.parent.get(matchControlName)?.value;
  }

  protected matchesValueFrom(matchTo: string): (abstractControl: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
        return this._matchValues(control, matchTo) ? null : { isMatching: false };
    };
}

  protected autoCapitalizeWords(controls: AbstractControl[]): void
  {
    controls.forEach(control => {
        this.autoCapitalizeSubs.add(
          control.valueChanges
            .pipe(debounceTime(350))
            .subscribe((value: string) => this.capitalizeWords(control, value)));
      });
  }

  ngOnDestroy(): void
  {
    this.autoCapitalizeSubs.unsubscribe();
  }
}
